import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

const TIERS = [
  {
    name: "Starter",
    price: "₹0",
    period: "/ forever",
    tag: "For exploring the platform",
    features: [
      "1 live strategy",
      "3 pre-built templates",
      "Risk in rupees",
      "Email support",
    ],
    cta: "Start free",
    popular: false,
  },
  {
    name: "Early Adopter",
    price: "₹499",
    period: "/ month",
    tag: "50% off locked for 12 months",
    features: [
      "Unlimited live strategies",
      "All pre-built templates",
      "Auto-transformative adjustments",
      "Custom leg builder",
      "Priority Telegram support",
    ],
    cta: "Join waitlist",
    popular: true,
  },
  {
    name: "Pro",
    price: "₹1,499",
    period: "/ month",
    tag: "For serious side-income traders",
    features: [
      "Everything in Early Adopter",
      "Multi-account execution",
      "Advanced backtesting",
      "1:1 strategy onboarding",
      "Dedicated account manager",
    ],
    cta: "Join waitlist",
    popular: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 md:py-28 bg-[#f8fafc] border-y border-[#e2e8f0]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.2em]">
            / PRICING
          </div>
          <h2 className="font-heading mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            Priced like a
            <br />
            <span className="text-[#084392]">monthly coffee run.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl">
            Lock early-adopter pricing for 12 months when you join the waitlist
            today.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`pricing-card-${t.name.toLowerCase().replace(" ", "-")}`}
              className={`relative rounded-2xl p-7 md:p-8 flex flex-col ${
                t.popular
                  ? "bg-[#084392] text-white border-2 border-[#084392] shadow-[0_30px_80px_-30px_rgba(8,67,146,0.5)]"
                  : "bg-white border border-[#e2e8f0]"
              }`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 bg-white text-[#084392] text-xs font-bold px-3 py-1 rounded-full border border-[#084392]/20">
                  <Sparkles size={12} /> MOST POPULAR
                </div>
              )}
              <div className="font-heading text-xl font-bold">{t.name}</div>
              <div className={`mt-1 text-sm ${t.popular ? "text-white/70" : "text-[#475569]"}`}>
                {t.tag}
              </div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-heading text-5xl font-black tracking-tighter">
                  {t.price}
                </span>
                <span className={t.popular ? "text-white/70" : "text-[#475569]"}>
                  {t.period}
                </span>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm">
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${
                        t.popular ? "text-white" : "text-[#084392]"
                      }`}
                    />
                    <span className={t.popular ? "text-white/90" : "text-[#0f172a]"}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#waitlist"
                data-testid={`pricing-cta-${t.name.toLowerCase().replace(" ", "-")}`}
                className={`mt-7 h-12 inline-flex items-center justify-center rounded-md font-semibold transition ${
                  t.popular
                    ? "bg-white text-[#084392] hover:bg-[#f8fafc]"
                    : "bg-[#084392] text-white hover:bg-[#06326e]"
                }`}
              >
                {t.cta}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
